import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { GetRawViewsQuery, useGetRawViewsQuery } from "@/apollo/types";

export type RawView = GetRawViewsQuery["getRawViews"][number];

// Context returning the live raw views and loading state
type RawViewsState = {
  rawViews: RawView[];
  loading: boolean;
};

const RawViewsContext = createContext<RawViewsState | null>(null);

// Context returning a stable getter function for raw views
type RawViewsGetter = () => RawView[];

const RawViewsGetterContext = createContext<RawViewsGetter | null>(null);

// Add a new context for the lookup function
type RawViewLookup = (id: string) => RawView | undefined;

const RawViewLookupContext = createContext<RawViewLookup | null>(null);

export const RawViewsProvider = ({ children }: { children: ReactNode }) => {
  const { data, loading } = useGetRawViewsQuery();

  const stateValue = useMemo<RawViewsState>(() => {
    const rawViews = data?.getRawViews ?? [];
    return {
      rawViews,
      loading,
    };
  }, [data?.getRawViews, loading]);

  // Create a ref to hold the current raw views value.
  const rawViewsRef = useRef<RawView[]>(stateValue.rawViews);
  const rawViewMapRef = useRef<Map<string, RawView>>(new Map());

  // Update the ref whenever the raw views part of our state changes.
  useEffect(() => {
    rawViewsRef.current = stateValue.rawViews;
    rawViewMapRef.current = new Map(
      stateValue.rawViews.map((view) => [view.viewId, view]),
    );
  }, [stateValue.rawViews]);

  // Stable getter function that always returns the latest raw views.
  const getRawViews = useCallback(() => {
    return rawViewsRef.current;
  }, []);

  const getRawViewById = useCallback((id: string) => {
    return rawViewMapRef.current.get(id);
  }, []);

  return (
    <RawViewsContext.Provider value={stateValue}>
      <RawViewsGetterContext.Provider value={getRawViews}>
        <RawViewLookupContext.Provider value={getRawViewById}>
          {children}
        </RawViewLookupContext.Provider>
      </RawViewsGetterContext.Provider>
    </RawViewsContext.Provider>
  );
};

// Hook for components that want the raw views and loading status (will re-render on changes)
export const useRawViews = () => {
  const context = useContext(RawViewsContext);
  if (!context) {
    throw new Error("useRawViews must be used within a RawViewsProvider");
  }
  return context;
};

// Hook for components that want a stable getter to get the raw views at any time
// without causing a component to re-render when changes occur.
export const useRawViewsGetter = () => {
  const getRawViews = useContext(RawViewsGetterContext);
  if (!getRawViews) {
    throw new Error("useRawViewsGetter must be used within a RawViewsProvider");
  }
  return getRawViews;
};

export const useRawViewLookup = () => {
  const lookup = useContext(RawViewLookupContext);
  if (!lookup) {
    throw new Error("useRawViewLookup must be used within a RawViewsProvider");
  }
  return lookup;
};
